import React, { useState, useEffect } from "react";

const WeeklyReport = () => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [modalLocation, setModalLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [filteredData, setFilteredData] = useState([]); // State for filtered data

  useEffect(() => {
    const today = new Date();
    const { startOfWeek, endOfWeek } = getWeekRange(today);
    setStartDate(startOfWeek);
    setEndDate(endOfWeek);
    fetchWeeklyData(startOfWeek, endOfWeek);
  }, []);

  useEffect(() => {
    // Filter the data based on search term
    if (searchTerm.trim() === "") {
      setFilteredData(weeklyData);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = weeklyData.map((dayData) => ({
        ...dayData,
        employees: dayData.employees.filter((employee) =>
          Object.values(employee)
            .join(" ")
            .toLowerCase()
            .includes(lowerCaseSearchTerm)
        ),
      })).filter((dayData) => dayData.employees.length > 0);

      setFilteredData(filtered);
    }
  }, [searchTerm, weeklyData]);

  const getWeekRange = (date) => {
    const currentDate = new Date(date);
    const dayOfWeek = currentDate.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() + diffToMonday);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return {
      startOfWeek: formatDate(startOfWeek),
      endOfWeek: formatDate(endOfWeek),
    };
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchWeeklyData = async (startOfWeek, endOfWeek) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/attendance/role-based/time-sheets/date-range?startDate=${startOfWeek}&endDate=${endOfWeek}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      const groupData = groupDataByDate(data);
      const dataForWeek = groupData.filter(
        (item) => item.date >= startOfWeek && item.date <= endOfWeek
      );
      setWeeklyData(dataForWeek);
      setFilteredData(dataForWeek); // Initialize filteredData with weeklyData
    } catch (error) {
      console.error("Error fetching weekly attendance data:", error);
      setWeeklyData([]);
      setFilteredData([]);
    }
  };

  const handlePrevWeek = () => {
    const prevWeekStart = new Date(startDate);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);

    const { startOfWeek, endOfWeek } = getWeekRange(prevWeekStart);
    setStartDate(startOfWeek);
    setEndDate(endOfWeek);
    fetchWeeklyData(startOfWeek, endOfWeek);
  };

  function groupDataByDate(dataArray) {
    const groupedData = dataArray.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = [];
      }
      acc[item.date].push(item);
      return acc;
    }, {});

    return Object.keys(groupedData).map((date) => ({
      date,
      employees: groupedData[date],
    }));
  }

  const handleNextWeek = () => {
    const nextWeekStart = new Date(startDate);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);

    const { startOfWeek, endOfWeek } = getWeekRange(nextWeekStart);
    setStartDate(startOfWeek);
    setEndDate(endOfWeek);
    fetchWeeklyData(startOfWeek, endOfWeek);
  };

  const showMapModal = (lat, long) => {
    setModalLocation({ lat, long });
  };

  const closeModal = () => {
    setModalLocation(null);
  };

  return (
    <>
      <div className="card shadow">
        <div className="card-header d-flex align-items-center justify-content-between bg-primary text-white">
          <h5>
            Weekly Attendance Report ({startDate} to {endDate})
          </h5>
          <div>
            <button
              className="btn btn-light btn-sm me-2"
              onClick={handlePrevWeek}
            >
              Prev Week
            </button>
            <button
              className="btn btn-light btn-sm"
              onClick={handleNextWeek}
              disabled={new Date(startDate).getTime() >= new Date().getTime()}
            >
              Next Week
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="mb-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search by employee name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {filteredData.length > 0 ? (
            filteredData.map((dayData, index) => (
              <div key={index} className="mb-4">
                <h6>{dayData.date}</h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Employee</th>
                        <th>Login Time</th>
                        <th>Logout Time</th>
                        <th>Login Location</th>
                        <th>Logout Location</th>
                        <th>Task</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayData.employees.map((employee, idx) => (
                        <tr key={idx}>
                          <td>{employee.employeeName}</td>
                          <td>{employee.loginTime}</td>
                          <td>{employee.logoutTime}</td>
                          <td>
                            {employee.loginLocation ? (
                              <span
                                className="text-primary cursor-pointer"
                                onClick={() =>
                                  showMapModal(
                                    employee.loginLocation.split(", ")[0].replace("Lat: ", ""),
                                    employee.loginLocation.split(", ")[1].replace("Long: ", "")
                                  )
                                }
                              >
                                View Map
                              </span>
                            ) : (
                              "Not Available"
                            )}
                          </td>
                          <td>
                            {employee.logoutLocation ? (
                              <span
                                className="text-primary cursor-pointer"
                                onClick={() =>
                                  showMapModal(
                                    employee.logoutLocation.split(", ")[0].replace("Lat: ", ""),
                                    employee.logoutLocation.split(", ")[1].replace("Long: ", "")
                                  )
                                }
                              >
                                View Map
                              </span>
                            ) : (
                              "Not Available"
                            )}
                          </td>
                          <td>{employee.taskDiscription}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            <p>No data available for this week.</p>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalLocation && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Location Map</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <iframe
                  width="100%"
                  height="400"
                  src={`https://www.google.com/maps?q=${modalLocation.lat},${modalLocation.long}&z=15&output=embed`}
                  frameBorder="0"
                  allowFullScreen
                  title="Google Map"
                ></iframe>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeeklyReport;
